import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { toast } from 'sonner';

export default function Users({ api }) {
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [profileFilter, setProfileFilter] = useState('all');
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    profile_id: ''
  });
  const [createdInfo, setCreatedInfo] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchProfilesList();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Você não tem permissão para gerenciar usuários');
      } else {
        toast.error('Erro ao carregar usuários');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfilesList = async () => {
    try {
      const response = await api.get('/profiles/list');
      setProfiles(response.data);
    } catch (error) {
      console.error('Erro ao carregar perfis:', error);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name.trim() || !newUser.email.trim() || !newUser.profile_id) {
      toast.error('Nome, email e perfil são obrigatórios');
      return;
    }

    if (newUser.password && newUser.password.length < 6) {
      toast.error('Senha deve ter pelo menos 6 caracteres');
      return;
    }

    try {
      const payload = {
        name: newUser.name,
        email: newUser.email,
        profile_id: newUser.profile_id,
      };
      // Envia a senha somente se o admin digitou uma; caso contrário o backend gera uma temporária
      if (newUser.password) payload.password = newUser.password;

      const response = await api.post('/users', payload);
      toast.success('Usuário criado com sucesso!');
      setShowAddModal(false);
      setNewUser({
        name: '',
        email: '',
        password: '',
        profile_id: ''
      });
      // Se o backend gerou uma senha temporária, exibe para o admin repassar
      if (response.data?.temp_password) {
        setCreatedInfo({
          name: response.data.name,
          email: response.data.email,
          temp_password: response.data.temp_password,
          email_sent: response.data.email_sent,
        });
      }
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao criar usuário';
      toast.error(message);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      const payload = {
        name: selectedUser.name,
        email: selectedUser.email,
        profile_id: selectedUser.profile_id,
        active: selectedUser.active
      };
      await api.put(`/users/${selectedUser.id}`, payload);
      toast.success('Usuário atualizado com sucesso!');
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao atualizar usuário';
      toast.error(message);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Tem certeza que deseja excluir o usuário "${userName}"? Esta ação não pode ser desfeita.`)) return;

    try {
      await api.delete(`/users/${userId}`);
      toast.success('Usuário excluído com sucesso!');
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao excluir usuário';
      toast.error(message);
    }
  };

  const [resendingId, setResendingId] = useState(null);

  const handleResendCredentials = async (user) => {
    if (!window.confirm(`Gerar uma nova senha temporária e reenviar o login para "${user.name}"? A senha atual do usuário deixará de funcionar.`)) return;

    setResendingId(user.id);
    try {
      const response = await api.post(`/users/${user.id}/resend-credentials`);
      if (response.data?.email_sent) {
        toast.success('Login e senha reenviados por e-mail!');
      } else {
        toast.warning('Não foi possível enviar o e-mail. Repasse as credenciais manualmente.');
      }
      setCreatedInfo({
        name: response.data.name,
        email: response.data.email,
        temp_password: response.data.temp_password,
        email_sent: response.data.email_sent,
        resent: true,
      });
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao reenviar credenciais';
      toast.error(message);
    } finally {
      setResendingId(null);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await api.put(`/users/${userId}`, { active: !currentStatus });
      toast.success(`Usuário ${!currentStatus ? 'ativado' : 'desativado'} com sucesso!`);
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao alterar status do usuário');
    }
  };

  const getProfileName = (profileId) => {
    const profile = profiles.find(p => p.id === profileId);
    return profile ? profile.name : 'Perfil não encontrado';
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewUser({...newUser, password});
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProfile = profileFilter === 'all' || user.profile_id === profileFilter;
    return matchesSearch && matchesProfile;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i} className="p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-slate-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-200 rounded w-1/4"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Gestão de Usuários</h1>
          <p className="text-slate-600 mt-1">Gerencie contas de usuário e permissões</p>
        </div>
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-user-button">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Novo Usuário
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Criar Novo Usuário</DialogTitle>
              <DialogDescription>Adicione um novo usuário ao sistema</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="user-name">Nome Completo *</Label>
                <Input
                  id="user-name"
                  data-testid="user-name-input"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  placeholder="Nome completo do usuário"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="user-email">Email *</Label>
                <Input
                  id="user-email"
                  type="email"
                  data-testid="user-email-input"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  placeholder="email@empresa.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="user-profile">Perfil *</Label>
                <Select 
                  value={newUser.profile_id} 
                  onValueChange={(value) => setNewUser({...newUser, profile_id: value})}
                >
                  <SelectTrigger data-testid="user-profile-select">
                    <SelectValue placeholder="Selecione um perfil" />
                  </SelectTrigger>
                  <SelectContent>
                    {profiles.map(profile => (
                      <SelectItem key={profile.id} value={profile.id}>
                        {profile.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="user-password">Senha (opcional)</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generatePassword}
                    data-testid="generate-password-button"
                  >
                    Gerar Senha
                  </Button>
                </div>
                <Input
                  id="user-password"
                  type="text"
                  data-testid="user-password-input"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  placeholder="Deixe em branco para gerar automaticamente"
                />
                <p className="text-xs text-slate-500">Se deixar em branco, uma senha temporária será gerada e enviada por e-mail. O usuário deverá trocá-la no primeiro acesso.</p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAddUser} className="btn-primary" data-testid="save-user-button">
                Criar Usuário
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="glass border-white/20">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search-users">Buscar Usuários</Label>
              <Input
                id="search-users"
                data-testid="search-users-input"
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-white/50"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="profile-filter">Filtrar por Perfil</Label>
              <Select value={profileFilter} onValueChange={setProfileFilter}>
                <SelectTrigger className="bg-white/50" data-testid="profile-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Perfis</SelectItem>
                  {profiles.map(profile => (
                    <SelectItem key={profile.id} value={profile.id}>
                      {profile.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <div className="space-y-4" data-testid="users-list">
        {filteredUsers.length === 0 ? (
          <Card className="glass border-white/20">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Nenhum usuário encontrado</h3>
              <p className="text-slate-500">
                {users.length === 0 ? 'Crie o primeiro usuário do sistema' : 'Ajuste os filtros para encontrar usuários'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredUsers.map((user) => (
            <Card key={user.id} className="glass border-white/20 card-hover" data-testid={`user-card-${user.id}`}>
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center space-x-4 min-w-0">
                    <Avatar className="h-12 w-12 shrink-0">
                      <AvatarImage src={`https://avatar.vercel.sh/${user.email}`} />
                      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-semibold">
                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1">
                        <h3 className="text-lg font-semibold text-slate-900 truncate">{user.name}</h3>
                        <Badge 
                          variant={user.active ? "default" : "secondary"}
                          className={user.active ? "bg-green-100 text-green-800 border-green-200" : ""}
                          data-testid={`user-status-${user.id}`}
                        >
                          {user.active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </div>
                      
                      <div className="space-y-1 text-sm text-slate-600">
                        <p>📧 {user.email}</p>
                        <p>👤 {user.profile_name}</p>
                        <p className="text-xs text-slate-500">
                          Início em {new Date(user.created_at).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center space-x-2 sm:mr-4">
                      <Label htmlFor={`status-${user.id}`} className="text-sm">Status:</Label>
                      <Switch
                        id={`status-${user.id}`}
                        checked={user.active}
                        onCheckedChange={() => toggleUserStatus(user.id, user.active)}
                        data-testid={`toggle-user-${user.id}`}
                      />
                    </div>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleResendCredentials(user)}
                      disabled={resendingId === user.id}
                      className="bg-white/60"
                      title="Reenviar Login e Senha"
                      data-testid={`resend-credentials-${user.id}`}
                    >
                      {resendingId === user.id ? (
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      )}
                      <span className="ml-1 hidden sm:inline">Reenviar Login</span>
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedUser(user);
                        setShowEditModal(true);
                      }}
                      className="bg-white/60"
                      data-testid={`edit-user-${user.id}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteUser(user.id, user.name)}
                      data-testid={`delete-user-${user.id}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Modal de senha temporária gerada */}
      <Dialog open={!!createdInfo} onOpenChange={(open) => { if (!open) setCreatedInfo(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{createdInfo?.resent ? 'Credenciais reenviadas' : 'Usuário criado com sucesso'}</DialogTitle>
            <DialogDescription>
              {createdInfo?.email_sent
                ? 'As credenciais foram enviadas por e-mail para o usuário.'
                : 'Não foi possível enviar o e-mail automaticamente. Repasse as credenciais abaixo ao usuário.'}
            </DialogDescription>
          </DialogHeader>
          {createdInfo && (
            <div className="space-y-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
                <div className="text-sm"><span className="font-semibold">Usuário:</span> {createdInfo.name}</div>
                <div className="text-sm"><span className="font-semibold">Login (e-mail):</span> {createdInfo.email}</div>
                {createdInfo.temp_password && (
                  <div className="text-sm flex items-center gap-2">
                    <span className="font-semibold">Senha temporária:</span>
                    <code className="px-2 py-1 rounded bg-white border border-slate-200 font-mono text-blue-600">{createdInfo.temp_password}</code>
                  </div>
                )}
              </div>
              {createdInfo.temp_password ? (
                <>
                  <p className="text-xs text-slate-500">O usuário deverá criar uma nova senha no primeiro acesso.</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard?.writeText(`Login: ${createdInfo.email}\nSenha temporária: ${createdInfo.temp_password}`);
                      toast.success('Credenciais copiadas!');
                    }}
                  >
                    Copiar credenciais
                  </Button>
                </>
              ) : (
                <p className="text-xs text-slate-500">As credenciais foram enviadas por e-mail. O usuário deverá criar uma nova senha no primeiro acesso.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCreatedInfo(null)} className="btn-primary">Concluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Usuário</DialogTitle>
            <DialogDescription>Atualize as informações do usuário</DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-user-name">Nome Completo</Label>
                <Input
                  id="edit-user-name"
                  value={selectedUser.name}
                  onChange={(e) => setSelectedUser({...selectedUser, name: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-user-email">Email</Label>
                <Input
                  id="edit-user-email"
                  type="email"
                  value={selectedUser.email}
                  onChange={(e) => setSelectedUser({...selectedUser, email: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-user-profile">Perfil</Label>
                <Select 
                  value={selectedUser.profile_id} 
                  onValueChange={(value) => setSelectedUser({...selectedUser, profile_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {profiles.map(profile => (
                      <SelectItem key={profile.id} value={profile.id}>
                        {profile.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-user-active"
                  checked={selectedUser.active}
                  onCheckedChange={(checked) => setSelectedUser({...selectedUser, active: checked})}
                />
                <Label htmlFor="edit-user-active">Usuário ativo</Label>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateUser} className="btn-primary">
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
